import React from 'react';
import { Helmet } from 'react-helmet-async';
import HeroSection from '../components/HeroSection';
import CTASection  from '../components/CTASection';
import Reveal      from '../components/Reveal';

/* -------------------------------------------------------
   SVG ICONS (inline, no dependency)
   ------------------------------------------------------- */
const icons = {
  Finance: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 2v20M17 5H9.5a3.5 3.5 0 100 7h5a3.5 3.5 0 110 7H6"/>
    </svg>
  ),
  Legal: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    </svg>
  ),
  Tech: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="3" width="20" height="14" rx="2"/>
      <path d="M8 21h8M12 17v4"/>
    </svg>
  ),
  Marketing: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10"/>
      <path d="M12 8v4l3 3"/>
    </svg>
  ),
  HR: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/>
    </svg>
  ),
  Analytics: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/>
      <line x1="6" y1="20" x2="6" y2="14"/><line x1="2" y1="20" x2="22" y2="20"/>
    </svg>
  ),
};

/* -------------------------------------------------------
   SHARED SERVICES DATA
   ------------------------------------------------------- */
const SERVICES = [
  {
    icon:  icons.Finance,
    title: 'Finance & Accounting',
    body:  'Consolidated financial reporting, brand-level P&L tracking, budgeting, cash management, and audit support are centralized at the holding company level. All portfolio brands operate within a consistent accounting framework.',
  },
  {
    icon:  icons.Legal,
    title: 'Legal & Compliance',
    body:  'Contract review, intellectual property management, regulatory compliance, privacy policy maintenance, and terms governance are managed centrally — protecting portfolio assets and ensuring consistent legal standards.',
  },
  {
    icon:  icons.Tech,
    title: 'Technology Infrastructure',
    body:  'Hosting, security, domain management, software licensing, access control, and data governance are standardized across the portfolio — reducing vendor fragmentation and establishing consistent technical baselines.',
  },
  {
    icon:  icons.Marketing,
    title: 'Marketing Operations',
    body:  'Brand standards management, cross-brand marketing guidance, campaign performance review, asset production support, and media planning consultation are available to all portfolio brands through centralized marketing operations.',
  },
  {
    icon:  icons.HR,
    title: 'HR & Talent Operations',
    body:  'Contractor and team management, onboarding documentation, compensation policy, non-disclosure agreements, and performance frameworks are administered centrally to maintain consistent people practices across the portfolio.',
  },
  {
    icon:  icons.Analytics,
    title: 'Analytics & Reporting',
    body:  'Portfolio-wide KPI frameworks, brand-level performance dashboards, data infrastructure management, and regular performance review cadences provide consistent visibility into how every brand is performing against expectations.',
  },
];

/* -------------------------------------------------------
   GOVERNANCE CADENCE
   ------------------------------------------------------- */
const CADENCE = [
  { freq: 'Weekly',    item: 'Brand-level operational check-ins and priority review.' },
  { freq: 'Monthly',   item: 'Financial performance review, KPI reporting, and risk register updates across all portfolio brands.' },
  { freq: 'Quarterly', item: 'Comprehensive portfolio performance review, capital allocation assessment, and strategic planning update.' },
  { freq: 'Annually',  item: 'Full portfolio audit, governance review, legal compliance confirmation, and annual operating plan development.' },
];

export default function OperatingModel() {
  return (
    <>
      <Helmet>
        <title>Operating Model — How Driven 2 LLC Operates Its Portfolio</title>
        <meta
          name="description"
          content="Driven 2 LLC operates its digital brand portfolio through centralized governance, shared services, structured reporting, and disciplined risk management. Learn how we build operational maturity at scale."
        />
        <meta property="og:title" content="Operating Model — How Driven 2 LLC Operates Its Portfolio" />
        <meta property="og:description" content="Centralized governance, shared services, security standards, and a disciplined reporting cadence — the operational infrastructure behind the Driven 2 LLC portfolio." />
        <meta property="og:url" content="https://www.driven2.net/operating-model" />
        <link rel="canonical" href="https://www.driven2.net/operating-model" />
      </Helmet>

      {/*
        ═══════════════════════════════════════════
        HERO PLACEHOLDER DIRECTION:
        "High-end operations and systems visual —
        architectural data-flow diagram overlay,
        premium dashboard interfaces, corporate
        glass-walled operations center atmosphere,
        institutional technology company feel.
        Deep navy with blue-lit accents."

        TO REPLACE: <HeroSection bgImage="/images/hero-operating-model.jpg" ... />
        ═══════════════════════════════════════════
      */}
      <HeroSection
        eyebrow="Operating Model"
        title={"Governance by Design.\nOperations by Discipline."}
        subtitle="The Driven 2 LLC operating model is built on the principle that strong infrastructure must precede scale. Our shared services architecture, governance cadences, and reporting standards are in place before the portfolio needs them."
        ctaPrimary={{ label: 'Get in Touch', to: '/contact' }}
        size="medium"
        bgImage="https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=1920&q=80"
      />

      {/* ═══════════════════════════════════════
          MODEL SUMMARY
          ═══════════════════════════════════════ */}
      <section className="section">
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.6fr', gap: 'clamp(2rem, 5vw, 5rem)', alignItems: 'start' }}>

            <Reveal>
              <div className="eyebrow">The Model</div>
              <h2 className="d-lg t-navy">How the Portfolio Operates</h2>
            </Reveal>

            <Reveal delay={0.1}>
              <p className="lead mb-6">
                Driven 2 LLC operates as a centralized holding company with operational
                functions distributed across shared service disciplines. Rather than requiring
                each portfolio brand to independently staff, manage, and fund foundational
                business infrastructure — finance, legal, technology, analytics — these
                functions are owned and operated at the holding company level and made
                available across the full portfolio.
              </p>
              <p className="lead mb-6">
                This architecture produces three compounding benefits. First, brands are freed
                to focus on their core value proposition rather than back-office management.
                Second, the holding company accumulates institutional knowledge and best
                practices that benefit every brand, not just one. Third, the portfolio as a
                whole maintains higher governance standards than any single brand could
                reasonably maintain independently.
              </p>
              <p className="lead">
                The result is a portfolio that is operationally efficient, consistently
                governed, and structurally ready for growth, partnership, or acquisition
                at any point in its development.
              </p>
            </Reveal>

          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════
          SHARED SERVICES
          ═══════════════════════════════════════ */}
      <section className="section section--dark">
        <div className="container">
          <Reveal>
            <div className="section-intro section-intro--center">
              <div className="eyebrow eyebrow--center eyebrow--light">Shared Services</div>
              <h2 className="d-lg t-white">Six Operational Functions, Centralized</h2>
              <p className="lead lead--white" style={{ maxWidth: '600px', margin: '0 auto' }}>
                Every portfolio brand accesses the full shared services suite. This
                eliminates per-brand overhead, standardizes quality, and ensures consistent
                governance regardless of brand stage or size.
              </p>
            </div>
          </Reveal>

          <div className="grid-3" style={{ marginTop: 'var(--sp-12)', gap: 'var(--sp-1)' }}>
            {SERVICES.map(({ icon, title, body }, i) => (
              <Reveal key={title} delay={i * 0.07}>
                <div
                  style={{
                    padding: 'var(--sp-8)',
                    borderTop: '1px solid rgba(255,255,255,0.07)',
                    borderRight: i % 3 !== 2 ? '1px solid rgba(255,255,255,0.07)' : 'none',
                  }}
                >
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      border: '1px solid rgba(184,150,90,0.4)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'var(--gold-400)',
                      marginBottom: 'var(--sp-5)',
                      flexShrink: 0,
                    }}
                  >
                    {icon}
                  </div>
                  <h3
                    style={{
                      fontFamily: 'var(--font-display)',
                      fontSize: 'var(--text-lg)',
                      fontWeight: 500,
                      color: 'var(--white)',
                      marginBottom: 'var(--sp-3)',
                    }}
                  >
                    {title}
                  </h3>
                  <p style={{ fontSize: 'var(--text-sm)', color: 'rgba(255,255,255,0.52)', lineHeight: 1.78 }}>
                    {body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════
          SECURITY & DATA
          ═══════════════════════════════════════ */}
      <section className="section section--alt">
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 'clamp(2rem, 5vw, 5rem)', alignItems: 'start' }}>

            <Reveal>
              <div className="eyebrow">Security &amp; Data Stewardship</div>
              <h2 className="d-lg t-navy mb-8">
                Protecting Portfolio Assets and Stakeholder Data
              </h2>
              <p className="lead mb-6">
                Data stewardship and security are not afterthoughts in our operating
                model — they are foundational obligations. Across every brand in the
                portfolio, we maintain consistent standards for how data is collected,
                stored, used, and protected.
              </p>
              <p className="lead mb-6">
                Access controls, vendor security assessments, privacy policy maintenance,
                and routine compliance reviews are part of our standing operating cadence.
                We hold our brand operators and technology vendors to the same standards
                we hold ourselves.
              </p>
              <p className="lead">
                Our privacy governance framework reflects current applicable standards
                including consideration of CCPA, GDPR applicability, and CAN-SPAM
                requirements — maintained centrally to ensure portfolio-wide compliance
                and to reduce the risk of brand-level exposure.
              </p>
            </Reveal>

            <Reveal delay={0.12}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
                {[
                  ['Access Controls',           'Role-based access and credential management across systems.'],
                  ['Vendor Assessment',          'Third-party vendor security reviews prior to integration.'],
                  ['Data Minimization',          'Collection limited to operationally necessary data only.'],
                  ['Privacy Policy Management',  'Maintained, version-controlled, and regularly reviewed.'],
                  ['Breach Response Protocol',   'Documented incident response procedure in place.'],
                  ['Regulatory Compliance',      'Active monitoring of applicable privacy and marketing regulations.'],
                ].map(([title, desc]) => (
                  <div
                    key={title}
                    style={{
                      padding: 'var(--sp-5)',
                      background: 'var(--white)',
                      border: '1px solid var(--gray-200)',
                      borderLeft: '2px solid var(--gold-500)',
                    }}
                  >
                    <div style={{ fontWeight: 600, fontSize: 'var(--text-sm)', color: 'var(--navy-800)', marginBottom: 'var(--sp-1)' }}>
                      {title}
                    </div>
                    <div style={{ fontSize: 'var(--text-xs)', color: 'var(--gray-600)', lineHeight: 1.7 }}>
                      {desc}
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>

          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════
          GOVERNANCE CADENCE
          ═══════════════════════════════════════ */}
      <section className="section section--dark">
        <div className="container">
          <Reveal>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr', gap: 'clamp(2rem, 5vw, 5rem)', alignItems: 'start' }}>
              <div>
                <div className="eyebrow eyebrow--light">Reporting Cadence</div>
                <h2 className="d-lg t-white mb-6">Governance on a Defined Schedule</h2>
                <p className="lead lead--white">
                  Consistent reporting is how governance standards become reality.
                  Our review cadence ensures that performance, risk, and capital
                  allocation decisions are never deferred indefinitely — they have
                  a scheduled moment in the process.
                </p>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
                {CADENCE.map(({ freq, item }, i) => (
                  <Reveal key={freq} delay={i * 0.08}>
                    <div
                      style={{
                        display: 'flex',
                        gap: 'var(--sp-6)',
                        padding: 'var(--sp-6) 0',
                        borderBottom: '1px solid rgba(255,255,255,0.07)',
                        alignItems: 'flex-start',
                      }}
                    >
                      <div
                        style={{
                          minWidth: '90px',
                          fontSize: 'var(--text-xs)',
                          fontWeight: 600,
                          letterSpacing: '0.12em',
                          textTransform: 'uppercase',
                          color: 'var(--gold-400)',
                          paddingTop: '2px',
                          flexShrink: 0,
                        }}
                      >
                        {freq}
                      </div>
                      <div style={{ fontSize: 'var(--text-sm)', color: 'rgba(255,255,255,0.62)', lineHeight: 1.75 }}>
                        {item}
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══════════════════════════════════════
          SCALABILITY & INTEGRATION
          ═══════════════════════════════════════ */}
      <section className="section">
        <div className="container">
          <Reveal>
            <div style={{ maxWidth: '860px' }}>
              <div className="eyebrow">Scalability &amp; Integration Readiness</div>
              <h2 className="d-lg t-navy mb-8">
                Built to Onboard New Brands Without Operational Disruption
              </h2>
              <p className="lead mb-6">
                One of the clearest measures of operational maturity is the ability to
                integrate a new asset without degrading the performance of existing ones.
                Driven 2 LLC maintains a documented onboarding playbook for new portfolio
                brands — a structured process that covers financial integration, technology
                migration, legal review, brand governance alignment, and team onboarding.
              </p>
              <p className="lead">
                Our shared services model is designed with portfolio growth in mind. As
                the portfolio expands, the marginal cost of governance support declines —
                and the institutional knowledge we carry compounds in value. This structural
                advantage is one of the reasons we view the holding company model as the
                right architecture for long-term digital brand ownership.
              </p>
            </div>
          </Reveal>

          <div style={{ marginTop: 'var(--sp-12)', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 'var(--sp-6)' }}>
            {[
              { title: 'Integration Playbook',   body: 'Documented step-by-step onboarding process for new acquisitions covering all functional areas.' },
              { title: 'Standard Controls',      body: 'Governance controls applied consistently across all brands, regardless of size or acquisition date.' },
              { title: 'Acquisition Readiness',  body: 'Operating discipline maintained so that any brand in the portfolio can be independently evaluated at any time.' },
            ].map(({ title, body }, i) => (
              <Reveal key={title} delay={i * 0.1}>
                <div className="card">
                  <h3 className="card__title">{title}</h3>
                  <p className="card__body" style={{ marginTop: 'var(--sp-3)' }}>{body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTASection
        eyebrow="Partnership &amp; Acquisition"
        headline="Interested in the Operating Model?"
        subtext="For investors, acquisition counterparties, or brand partners evaluating how Driven 2 LLC operates, we welcome direct conversations. We are transparent about our model and our standards."
        primaryCTA={{ label: 'Contact Driven 2 LLC', to: '/contact' }}
        secondaryCTA={{ label: 'View Portfolio', to: '/brands' }}
        variant="dark"
      />
    </>
  );
}
